import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Grid, List, X, Check, ChevronRight, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ProductCard } from '../components/ProductCard';
import { RewardCard } from '../components/RewardCard';
import SEO from '../components/SEO';
import { fetchAllRewards, getRewardStartingPaise } from '../lib/rewardCatalogue';
import { getRewardName } from '../lib/rewardImage';
import { CategoryHeroBanner } from '../components/CategoryHeroBanner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

// The rewards catalogue is paged client-side, so a page costs no network round-trip.
// 24 keeps the pager down to a scannable number of buttons (277 rewards today would
// be 24 pages at the product page size of 12).
const REWARDS_PER_PAGE = 24;

export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derived, not state. As state this lagged a render behind the slug, and the brand
  // effect below read it while it was still false — so opening /category/gift-cards
  // populated the Brand filter from the phone catalogue (ADIDAS, POLAROID, …) and
  // never cleared it.
  const isGiftCardCategory = slug === 'gift-cards' || slug === 'gift-card';

  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  // Kept separately so the banner can be fetched without waiting on products.
  const [categoryId, setCategoryId] = useState(null);
  const [hasBanner, setHasBanner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [availableBrands, setAvailableBrands] = useState([]);
  // Total from the API, so the toolbar reports the whole result set rather than
  // the number of rows loaded so far.
  const [totalProducts, setTotalProducts] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [density, setDensity] = useState('comfortable');   // comfortable | compact
  const sentinelRef = useRef(null);

  const sort = searchParams.get('sort') || 'newest';
  const minPrice = searchParams.get('minPrice');
  const maxPrice = searchParams.get('maxPrice');
  // The API takes a single `brand`. This used to be a `brands` array that was parsed
  // and then never used or rendered anywhere — dead code advertising nothing.
  const brand = searchParams.get('brand') || '';

  const selectBrand = (value) => {
    if (value && value !== brand) searchParams.set('brand', value);
    else searchParams.delete('brand');
    setSearchParams(searchParams);
  };

  const query = searchParams.get('q');
  const [localSearch, setLocalSearch] = useState(query || '');
  const [debouncedSearch, setDebouncedSearch] = useState(query || '');

  // Keep local input in sync if URL query changes from top navbar search
  useEffect(() => {
    setLocalSearch(query || '');
    setDebouncedSearch(query || '');
  }, [query]);

  // Debounce the local search for local filtering or API calls without touching URL
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(localSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch]);

  const isFeatured = searchParams.get('featured');
  const isBestseller = searchParams.get('bestseller');
  const isTrending = searchParams.get('trending');
  const inStockOnly = searchParams.get('inStock') === '1';

  // Was a hardcoded checked={true} with no handler — decorative, and it advertised a
  // filter the store did not actually apply.
  const toggleInStock = (checked) => {
    if (checked) searchParams.set('inStock', '1');
    else searchParams.delete('inStock');
    setSearchParams(searchParams);
  };

  // One primitive standing in for "any filter changed", so the effects below have a
  // statically checkable dependency instead of eight inline searchParams lookups.
  const filterSignature = [sort, minPrice, maxPrice, brand, debouncedSearch, isFeatured, isBestseller, isTrending, inStockOnly].join('|');

  // Any filter change puts the user back on page 1 — page 3 of the old result set is
  // meaningless against the new one.
  useEffect(() => {
    setPage(1);
  }, [slug, filterSignature]);

  /**
   * Rewards are cursor-paginated by Razorpay: each response carries 100 items plus a
   * `next_page_id`, and `count` is the real catalogue size (277 at the time of
   * writing). Reading only the first response — which is what this used to do —
   * silently hid nearly two thirds of the store.
   *
   * The whole catalogue is walked once and paged in memory rather than per page,
   * because the cursor is forward-only: there is no way to jump to page 5 without
   * replaying pages 1-4, and no single-reward endpoint to fall back on. Deliberately
   * keyed on the category alone, so paging never refetches.
   */
  useEffect(() => {
    if (!isGiftCardCategory) return;

    let cancelled = false;

    const fetchRewards = async () => {
      setLoading(true);

      let collected = [];

      try {
        // Paints each page as it lands rather than holding a blank grid for
        // three round-trips — the first 100 are usable while the rest arrive.
        const { items } = await fetchAllRewards({
          isCancelled: () => cancelled,
          onPage: (soFar) => {
            if (cancelled) return;
            setRewards([...soFar]);
            setLoading(false);
          },
        });
        collected = items;
      } catch (error) {
        console.error('Failed to fetch gift cards:', error);
      }

      // A later slug won the race — its own run owns the state now.
      if (cancelled) return;

      setRewards(collected);
      setLoading(false);
    };

    fetchRewards();
    return () => { cancelled = true; };
  }, [isGiftCardCategory]);

  /**
   * Category id, resolved on its own rather than as a side effect of loading
   * products. The products effect returns early for gift cards, so anything
   * that depended on it — the banner, in particular — never got an id on that
   * page at all.
   */
  useEffect(() => {
    if (!slug) { setCategoryId(null); return; }
    let cancelled = false;
    fetch(`${API}/categories/slug/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.category_id) setCategoryId(d.category_id); })
      .catch(() => { /* no banner is a fine outcome */ });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    // Rewards have their own fetch above.
    if (isGiftCardCategory) return;

    const fetchData = async () => {
      // Appending must not blank the grid the customer is already reading.
      if (page === 1) setLoading(true); else setLoadingMore(true);

      try {
        let categoryId = null;

        if (slug) {
          // Fetch category if slug is provided
          const catResponse = await fetch(`${API}/categories/slug/${slug}`);
          if (!catResponse.ok) throw new Error('Category not found');
          const catData = await catResponse.json();
          setCategory(catData);
          categoryId = catData.category_id;
        } else {
          setCategory(null);
        }

        // Then fetch products
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '12',
          sort
        });

        if (categoryId) params.append('category', categoryId);
        if (minPrice) params.append('min_price', minPrice);
        if (maxPrice) params.append('max_price', maxPrice);
        if (debouncedSearch) params.append('search', debouncedSearch);
        if (isFeatured) params.append('featured', isFeatured);
        if (isBestseller) params.append('bestseller', isBestseller);
        if (isTrending) params.append('trending', isTrending);
        // Same param the admin product list uses for its stock filter.
        if (inStockOnly) params.append('stock_status', 'in');
        if (brand) params.append('brand', brand);

        const prodResponse = await fetch(`${API}/products?${params}`);
        const prodData = await prodResponse.json();

        const batch = prodData.products || [];
        // Page 1 replaces, later pages append. Any filter change resets `page`
        // to 1 (effect above), so "page 1" is exactly the reset signal — no
        // extra flag has to be kept in sync with it.
        setProducts((prev) => (page === 1 ? batch : [...prev, ...batch]));
        setTotalPages(prodData.pages || 1);
        setTotalProducts(prodData.total ?? null);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setProducts([]);
        if (slug) setCategory(null);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchData();
  }, [isGiftCardCategory, slug, page, sort, minPrice, maxPrice, brand, debouncedSearch, isFeatured, isBestseller, isTrending, inStockOnly]);

  /**
   * Gift cards are filtered and sorted here rather than by the API — the whole
   * catalogue is already in memory (the Razorpay cursor has no filter or sort
   * params to pass anyway). Until this existed the sidebar rendered controls
   * that changed the URL and nothing else.
   */
  const filteredRewards = useMemo(() => {
    if (!isGiftCardCategory) return [];

    const min = minPrice ? Number(minPrice) : null;
    const max = maxPrice ? Number(maxPrice) : null;
    const needle = (debouncedSearch || '').trim().toLowerCase();

    const list = rewards.filter((reward) => {
      const price = getRewardStartingPaise(reward) / 100;
      if (min !== null && price < min) return false;
      if (max !== null && price > max) return false;
      if (needle && !getRewardName(reward).toLowerCase().includes(needle)) return false;
      return true;
    });

    // `newest` is catalogue order — the provider returns them newest-first and
    // carries no timestamp to sort on. Same for `rating`: there are no reviews
    // on rewards, which is why the option is hidden on this page.
    if (sort === 'price_low' || sort === 'price_high') {
      const direction = sort === 'price_low' ? 1 : -1;
      list.sort((a, b) => direction * (getRewardStartingPaise(a) - getRewardStartingPaise(b)));
    }

    return list;
  }, [isGiftCardCategory, rewards, minPrice, maxPrice, debouncedSearch, sort]);

  // Accumulated, not a single page: Load More has to grow the grid on both
  // branches or the control would mean two different things.
  const pagedRewards = filteredRewards.slice(0, page * REWARDS_PER_PAGE);

  // Derived for gift cards, so narrowing the filter shrinks the pager instead of
  // leaving Load More pointing at pages the filtered list no longer has.
  const effectiveTotalPages = isGiftCardCategory
    ? Math.max(1, Math.ceil(filteredRewards.length / REWARDS_PER_PAGE))
    : totalPages;

  const shownCount = isGiftCardCategory ? pagedRewards.length : products.length;
  const resultCount = isGiftCardCategory ? filteredRewards.length : (totalProducts ?? products.length);
  const hasMore = page < effectiveTotalPages;

  const loadMore = useCallback(() => {
    if (loading || loadingMore || page >= effectiveTotalPages) return;
    setPage((p) => p + 1);
  }, [loading, loadingMore, page, effectiveTotalPages]);

  /**
   * Infinite scroll, with the Load More button kept as the real control.
   *
   * The observer only fires when the sentinel is actually reached, so a
   * customer who scrolls fast still gets pages in order. Keyboard and
   * screen-reader users never trigger an IntersectionObserver at all — the
   * button is what serves them, which is why it is not decorative.
   */
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '600px 0px' }   // start the next page before the grid runs out
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  const gridClass = density === 'compact'
    ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2'
    : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 md:gap-3';

  // Chips that map onto filters the API already accepts.
  const quickChips = [
    { key: 'bestseller', label: 'Bestsellers', active: !!isBestseller },
    { key: 'trending', label: 'Trending', active: !!isTrending },
    { key: 'featured', label: 'Featured', active: !!isFeatured },
    { key: 'inStock', label: 'In stock', active: inStockOnly, value: '1' },
  ];

  const toggleChip = (chip) => {
    if (chip.active) searchParams.delete(chip.key);
    else searchParams.set(chip.key, chip.value || 'true');
    setSearchParams(searchParams);
  };



  const handleSortChange = (value) => {
    searchParams.set('sort', value);
    setSearchParams(searchParams);
  };

  const handlePriceFilter = (min, max) => {
    const currentMin = searchParams.get('minPrice');
    const currentMax = searchParams.get('maxPrice');

    // If same range is selected, untoggle it
    if (String(min) === currentMin && (max === null ? !currentMax : String(max) === currentMax)) {
      searchParams.delete('minPrice');
      searchParams.delete('maxPrice');
    } else {
      if (min !== null) searchParams.set('minPrice', min);
      else searchParams.delete('minPrice');

      if (max !== null) searchParams.set('maxPrice', max);
      else searchParams.delete('maxPrice');
    }

    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchParams({ sort: 'newest' });
    // The box drops `q` from the URL on the first keystroke, so from then on the
    // term lives only here. Resetting the params alone left "No gift cards found"
    // on screen with a Clear Filters button that did nothing.
    setLocalSearch('');
    setDebouncedSearch('');
  };

  /**
   * There is no brands endpoint, so the list is derived from the products in this
   * category. Deliberately unfiltered by brand — otherwise selecting one brand would
   * collapse the list to just that brand and you could never switch.
   *
   * Brand values in the catalogue are dirty (' VIVO', '\tOPPO', 'Realme' vs 'REALME'),
   * so they're trimmed and de-duplicated case-insensitively for display. The most
   * frequent raw spelling is what gets sent to the API.
   */
  useEffect(() => {
    if (isGiftCardCategory) return;

    const loadBrands = async () => {
      try {
        const params = new URLSearchParams({ page: '1', limit: '200' });
        if (category?.category_id) params.append('category', category.category_id);
        if (debouncedSearch) params.append('search', debouncedSearch);

        const res = await fetch(`${API}/products?${params}`);
        if (!res.ok) return;
        const data = await res.json();

        const counts = new Map();
        (data.products || []).forEach((p) => {
          const raw = (p.brand || '').trim();
          if (!raw) return;
          const key = raw.toUpperCase();
          const entry = counts.get(key) || { label: raw, value: raw, count: 0 };
          entry.count += 1;
          counts.set(key, entry);
        });

        setAvailableBrands(
          [...counts.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
        );
      } catch (error) {
        console.error('Could not derive brand list:', error);
      }
    };

    loadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.category_id, debouncedSearch, isGiftCardCategory]);

  // Gift cards need their own buckets: they start from ₹10 and most of the
  // catalogue sits under ₹1,000, so the phone ranges would put nearly every
  // card in one bucket and leave the other four empty.
  const priceRanges = isGiftCardCategory
    ? [
      { label: 'Under ₹250', min: 0, max: 250 },
      { label: '₹250 - ₹500', min: 250, max: 500 },
      { label: '₹500 - ₹1,000', min: 500, max: 1000 },
      { label: '₹1,000 - ₹5,000', min: 1000, max: 5000 },
      { label: 'Above ₹5,000', min: 5000, max: null }
    ]
    : [
      { label: 'Under ₹1,000', min: 0, max: 1000 },
      { label: '₹1,000 - ₹5,000', min: 1000, max: 5000 },
      { label: '₹5,000 - ₹20,000', min: 5000, max: 20000 },
      { label: '₹20,000 - ₹50,000', min: 20000, max: 50000 },
      { label: 'Above ₹50,000', min: 50000, max: null }
    ];


  const formatPageTitle = (text) => {
    if (!text) return '';
    return text.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const rawTitle = isGiftCardCategory ? 'Gift Cards' : (category?.name || (debouncedSearch ? `Search: ${debouncedSearch}` : slug || (isFeatured ? 'Featured Products' : isBestseller ? 'Best Sellers' : isTrending ? 'Trending Now' : 'All Products')));
  const pageTitle = formatPageTitle(rawTitle);

  const pageDescription = isGiftCardCategory
    ? 'Discover our premium range of digital gift cards for your favorite brands.' 
    : (category?.description || (debouncedSearch ? `Results for "${debouncedSearch}"` : 'Discover our premium collection of products.'));

  return (
    <div className="min-h-screen bg-white" data-testid="category-page">
      <SEO
        title={pageTitle}
        description={pageDescription}
        // Search result pages are thin and infinitely variable — keep them out of
        // the index so they don't compete with the real category pages.
        noIndex={!!debouncedSearch}
      />
      {/* Hero — deliberately short. Every pixel here is a row of products the
          customer has to scroll past before seeing anything buyable. */}
      {/* Published from Admin → Banner Management. Renders nothing when the
          category has no banner, so the page never depends on one. Gift cards
          were excluded here by mistake — they are a category like any other. */}
      {/* Edge to edge, but capped. At the artwork's own 2.74:1 an uncapped
          full-bleed hero stood ~535px on a laptop and pushed the entire product
          grid below the fold, on the page whose job is to show products. The cap
          crops the art rather than shrinking it, because a contained card left
          gutters on all four sides and read worse than the crop does.

          If a category's art is authored at ~4:1 instead, the cap stops biting
          and nothing is cropped at all — that is the better fix per banner. */}
      <CategoryHeroBanner categoryId={categoryId} maxHeight={400} onLoaded={setHasBanner} />

      <section className="border-b border-slate-100 bg-slate-50">
        <div className={`mx-auto max-w-[1600px] px-4 md:px-6 ${hasBanner ? 'py-3' : 'py-5 md:py-6'}`}>
          {!hasBanner && (
            <p className="font-body text-[11px] font-semibold uppercase tracking-widest text-primary">
              Shop Collection
            </p>
          )}
          <h1 className={`font-body font-extrabold tracking-[-0.02em] text-secondary ${
            hasBanner ? 'text-[18px] md:text-[20px]' : 'mt-1 text-[22px] md:text-[28px]'}`}>
            {pageTitle}
          </h1>
          {!hasBanner && (
            <p className="mt-1 line-clamp-2 max-w-3xl font-body text-[13px] text-muted-foreground md:text-sm">
              {pageDescription}
            </p>
          )}
        </div>

        {/* Brand strip — horizontally scrollable, drawn from the brands actually
            present in this category. Hidden when there is nothing to choose. */}
        {availableBrands.length > 1 && (
          <div className="mx-auto max-w-[1600px] px-4 pb-3 md:px-6">
            <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                onClick={() => selectBrand('')}
                className={`flex-shrink-0 rounded-full border px-3 py-1.5 font-body text-[12px] font-semibold transition-colors ${
                  !brand ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-white text-secondary hover:border-slate-300'}`}
              >
                All brands
              </button>
              {availableBrands.map((b) => (
                <button
                  key={b.value}
                  onClick={() => selectBrand(b.value)}
                  className={`flex-shrink-0 rounded-full border px-3 py-1.5 font-body text-[12px] font-semibold transition-colors ${
                    brand === b.value ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-white text-secondary hover:border-slate-300'}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="mx-auto max-w-[1600px] px-4 py-5 md:px-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:gap-6">
          {/* Desktop Filters Sidebar */}
          <aside className="sticky top-20 hidden max-h-[calc(100vh-6rem)] w-[260px] flex-shrink-0 self-start overflow-y-auto lg:block [scrollbar-width:thin]" data-testid="filters-sidebar">
            <div className="rounded-2xl border border-slate-200/70 bg-white p-4">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-5 h-5 text-secondary" />
                  <h3 className="font-heading text-xl text-secondary">Filters</h3>
                </div>
                {(minPrice || maxPrice || brand || inStockOnly) && (
                  <button
                    onClick={clearFilters}
                    className="text-xs font-body font-bold text-primary hover:text-primary/80 transition-colors uppercase tracking-widest"
                  >
                    Reset
                  </button>
                )}
              </div>

              {/* Price Filter */}
              <div className="space-y-6">
                <div>
                  <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-5 flex items-center gap-2">
                    Price Range
                  </h4>
                  <div className="space-y-2">
                    {priceRanges.map((range, idx) => {
                      const isActive = minPrice === String(range.min) && (range.max ? maxPrice === String(range.max) : !maxPrice);
                      return (
                        <button
                          key={idx}
                          onClick={() => handlePriceFilter(range.min, range.max)}
                          className={`w-full flex items-center justify-between group px-4 py-3 rounded-xl transition-all font-body text-sm ${isActive
                            ? 'bg-primary text-white shadow-md'
                            : 'bg-white hover:bg-white text-muted-foreground hover:text-secondary border border-transparent hover:border-gray-100'
                            }`}
                        >
                          <span>{range.label}</span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${isActive ? 'translate-x-1' : 'opacity-0 group-hover:opacity-100'}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {availableBrands.length > 1 && (
                  <div className="pt-6 border-t border-gray-100">
                    <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-4">Brand</h4>
                    <div className="space-y-1 max-h-64 overflow-y-auto custom-scrollbar pr-1">
                      {availableBrands.map((b) => (
                        <button
                          key={b.value}
                          onClick={() => selectBrand(b.value)}
                          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl transition-all font-body text-sm ${brand === b.value
                            ? 'bg-primary text-white shadow-md'
                            : 'bg-white hover:bg-white text-muted-foreground hover:text-secondary border border-transparent hover:border-gray-100'
                            }`}
                          data-testid={`brand-${b.value}`}
                        >
                          <span className="truncate">{b.label}</span>
                          <span className={`text-xs flex-shrink-0 ml-2 ${brand === b.value ? 'text-white/70' : 'text-muted-foreground/60'}`}>
                            {b.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rewards carry no stock signal — the provider only returns
                    cards it can actually issue — so the control is hidden here
                    rather than left as a switch that filters nothing. */}
                {!isGiftCardCategory && (
                  <div className="pt-6 border-t border-gray-100">
                    <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-4">Availability</h4>
                    <label className="flex items-center justify-between group cursor-pointer">
                      <span className="text-sm text-muted-foreground group-hover:text-secondary font-body">In Stock Only</span>
                      <Checkbox checked={inStockOnly} onCheckedChange={toggleInStock} />
                    </label>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Toolbar — count, quick chips, density and sort in one row. */}
            <div className="mb-4 flex flex-col gap-2.5 border-b border-slate-100 pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 font-body text-[13px] font-semibold text-secondary transition-colors hover:bg-slate-50 lg:hidden"
                    data-testid="mobile-filters-btn"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
                  </button>
                  <p className="font-body text-[13px] text-muted-foreground">
                    <span className="font-bold text-secondary">{shownCount}</span>
                    {resultCount > shownCount && <> of <span className="font-bold text-secondary">{resultCount}</span></>}
                    {' '}{isGiftCardCategory ? 'gift card' : 'product'}
                    {Math.max(resultCount, shownCount) === 1 ? '' : 's'}
                    {/* Says the search actually ran — with no echo of the term, a
                        narrowed grid looks the same as a broken box. */}
                    {debouncedSearch && <> for “<span className="font-bold text-secondary">{debouncedSearch}</span>”</>}
                  </p>
                </div>

                <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
                  {/* Filled rather than outlined: on a white grid a hairline border
                      reads as a label, not a control — and it used to be `hidden
                      sm:block`, so on a phone there was no way at all to search 263
                      cards. It now wraps onto its own full-width line instead. */}
                  <div className="relative order-last w-full sm:order-none sm:mr-2 sm:w-64 md:w-80">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <input
                      type="search"
                      aria-label={`Search ${isGiftCardCategory ? 'gift cards' : 'products'}`}
                      placeholder={`Search ${isGiftCardCategory ? 'gift cards' : 'products'}...`}
                      value={localSearch}
                      onChange={(e) => {
                        setLocalSearch(e.target.value);
                        if (searchParams.has('q')) {
                          searchParams.delete('q');
                          setSearchParams(searchParams, { replace: true });
                        }
                      }}
                      className="h-10 w-full rounded-lg border border-slate-300 bg-slate-50 pl-9 pr-9 font-body text-[13px] text-secondary outline-none transition-all placeholder:text-slate-500 focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 [&::-webkit-search-cancel-button]:hidden"
                      data-testid="category-search"
                    />
                    {localSearch && (
                      <button
                        type="button"
                        onClick={() => {
                          setLocalSearch('');
                          if (searchParams.has('q')) {
                            searchParams.delete('q');
                            setSearchParams(searchParams, { replace: true });
                          }
                        }}
                        aria-label="Clear search"
                        className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-slate-200 hover:text-secondary"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Grid density. Purely a view preference — it changes no query. */}
                  <div className="hidden items-center gap-0.5 rounded-lg bg-slate-100 p-0.5 sm:flex">
                    <button
                      onClick={() => setDensity('comfortable')}
                      aria-label="Comfortable grid"
                      aria-pressed={density === 'comfortable'}
                      className={`grid h-7 w-7 place-items-center rounded-md transition-colors ${
                        density === 'comfortable' ? 'bg-white text-secondary shadow-sm' : 'text-muted-foreground hover:text-secondary'}`}
                    >
                      <Grid className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDensity('compact')}
                      aria-label="Compact grid"
                      aria-pressed={density === 'compact'}
                      className={`grid h-7 w-7 place-items-center rounded-md transition-colors ${
                        density === 'compact' ? 'bg-white text-secondary shadow-sm' : 'text-muted-foreground hover:text-secondary'}`}
                    >
                      <List className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <Select value={sort} onValueChange={handleSortChange}>
                    <SelectTrigger className="h-9 w-[168px] rounded-lg border-slate-200 bg-white px-3 font-body text-[13px]" data-testid="sort-select">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="newest">Newest First</SelectItem>
                      <SelectItem value="price_low">Price: Low to High</SelectItem>
                      <SelectItem value="price_high">Price: High to Low</SelectItem>
                      {/* Rewards have no reviews, so there is nothing to rank by. */}
                      {!isGiftCardCategory && <SelectItem value="rating">Best Rating</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Quick filters — every chip maps to a param the API already takes. */}
              {!isGiftCardCategory && (
                <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {quickChips.map((chip) => (
                    <button
                      key={chip.key}
                      onClick={() => toggleChip(chip)}
                      className={`flex-shrink-0 rounded-full border px-2.5 py-1 font-body text-[12px] font-semibold transition-colors ${
                        chip.active
                          ? 'border-primary bg-primary/[0.08] text-primary'
                          : 'border-slate-200 bg-white text-muted-foreground hover:border-slate-300 hover:text-secondary'}`}
                    >
                      {chip.active && <Check className="mr-1 inline h-3 w-3" />}
                      {chip.label}
                    </button>
                  ))}
                  {(minPrice || maxPrice || brand || inStockOnly || isBestseller || isTrending || isFeatured) && (
                    <button
                      onClick={clearFilters}
                      className="flex-shrink-0 rounded-full px-2.5 py-1 font-body text-[12px] font-semibold text-muted-foreground underline-offset-2 hover:text-destructive hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Filters Modal */}
            {showFilters && (
              <div className="lg:hidden fixed inset-0 z-50 bg-white animate-in slide-in-from-bottom duration-300" data-testid="mobile-filters-modal">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-5 h-5 text-secondary" />
                    <h3 className="font-heading text-xl text-secondary">Filters</h3>
                  </div>
                  <button onClick={() => setShowFilters(false)} className="p-2 hover:bg-surface rounded-full">
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(100vh-140px)]">
                  {/* Price Filter */}
                  <div className="mb-10">
                    <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-6">Price Range</h4>
                    <div className="space-y-3">
                      {priceRanges.map((range, idx) => {
                        const isActive = minPrice === String(range.min) && (range.max ? maxPrice === String(range.max) : !maxPrice);
                        return (
                          <button
                            key={idx}
                            onClick={() => handlePriceFilter(range.min, range.max)}
                            className={`w-full flex items-center justify-between px-6 py-4 rounded-2xl transition-all font-body ${isActive
                              ? 'bg-primary text-white shadow-lg'
                              : 'bg-surface text-secondary border border-transparent'
                              }`}
                          >
                            <span className="font-medium">{range.label}</span>
                            {isActive && <Check className="w-5 h-5" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {availableBrands.length > 1 && (
                    <div className="mb-10 pt-8 border-t border-gray-100">
                      <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-6">Brand</h4>
                      <div className="flex flex-wrap gap-2">
                        {availableBrands.map((b) => (
                          <button
                            key={b.value}
                            onClick={() => selectBrand(b.value)}
                            className={`px-4 py-2.5 rounded-full font-body text-sm transition-all ${brand === b.value
                              ? 'bg-primary text-white shadow-md'
                              : 'bg-surface text-secondary'
                              }`}
                          >
                            {b.label} <span className="opacity-60">{b.count}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {!isGiftCardCategory && (
                    <div className="pt-8 border-t border-gray-100">
                      <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-6">Other Options</h4>
                      <div className="space-y-4">
                        <label className="flex items-center justify-between p-4 bg-surface rounded-2xl">
                          <span className="font-body font-medium text-secondary text-sm">In Stock Only</span>
                          <Checkbox checked={inStockOnly} onCheckedChange={toggleInStock} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      className="flex-1 h-14 rounded-full font-body font-semibold border-gray-200"
                      onClick={clearFilters}
                    >
                      Reset All
                    </Button>
                    <Button
                      className="flex-1 h-14 rounded-full bg-primary font-body font-semibold shadow-lg shadow-primary/20"
                      onClick={() => setShowFilters(false)}
                    >
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Products/Rewards Grid */}
            {loading ? (
              <div className={gridClass}>
                {[...Array(12)].map((_, idx) => (
                  <div key={idx} className="aspect-[3/4] animate-pulse rounded-xl bg-slate-100" />
                ))}
              </div>
            ) : isGiftCardCategory && pagedRewards.length > 0 ? (
              <div className={gridClass} data-testid="rewards-grid">
                {pagedRewards.map((reward, idx) => (
                  <RewardCard key={`${reward.id || 'reward'}-${idx}`} reward={reward} />
                ))}
              </div>
            ) : !isGiftCardCategory && products.length > 0 ? (
              <div className={`${gridClass} w-full`} data-testid="products-grid">
                {products.map((product, idx) => (
                  <ProductCard key={`${product.product_id || 'prod'}-${idx}`} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-muted-foreground font-body text-lg">
                  No {isGiftCardCategory ? 'gift cards' : 'products'} found
                  {debouncedSearch && <> for “<span className="font-semibold text-secondary">{debouncedSearch}</span>”</>}
                </p>
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="mt-4 rounded-full"
                >
                  Clear Filters
                </Button>
              </div>
            )}

            {/*
              Load More over the same paginated endpoint — `page` still
              increments by one and `limit` is unchanged. The API contract is
              identical; only the way results are shown has changed, from
              replacing the grid to growing it.
            */}
            {hasMore && (
              <>
                <div ref={sentinelRef} aria-hidden="true" className="h-px" />
                <div className="mt-6 flex flex-col items-center gap-2">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="rounded-xl border border-slate-200 bg-white px-6 py-2.5 font-body text-sm font-semibold text-secondary transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:opacity-60"
                    data-testid="load-more"
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                  <p className="font-body text-[11px] text-muted-foreground">
                    Showing {shownCount}{resultCount > shownCount ? ` of ${resultCount}` : ''}
                  </p>
                </div>
              </>
            )}

            {loadingMore && (
              <div className={`${gridClass} mt-3`} aria-hidden="true">
                {[...Array(6)].map((_, idx) => (
                  <div key={idx} className="aspect-[3/4] animate-pulse rounded-xl bg-slate-100" />
                ))}
              </div>
            )}

          </main>
        </div>
      </div>
    </div>
  );
}
